import { compareAsc, compareDesc, differenceInCalendarDays, differenceInDays, differenceInSeconds, format, startOfDay, startOfWeek } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

// Month names arrays - moved from frontend/src/reusable/utils/date-utils.ts
export const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export const monthNamesShort = monthNames.map(month => month.slice(0, 3))

/**
 * How far back the calendar fetch window reaches. 24 hours is the proven minimum — the most anyone
 * can be into their own local day is 24 hours, whatever their offset — and 48 leaves headroom for
 * DST shifts and clock skew.
 */
export const ROLLING_FETCH_WINDOW_HOURS = 48

/**
 * The floor of the rolling calendar fetch window: `now − ROLLING_FETCH_WINDOW_HOURS`. A fetch size
 * rather than a day boundary, so it needs no user timezone and holds for every market.
 */
export function rollingFetchWindowStart(): Date {
   return new Date(Date.now() - ROLLING_FETCH_WINDOW_HOURS * 60 * 60 * 1000)
}

/**
 * How far forward the calendar fetch window reaches — six "Show more" presses at the list's widest
 * day-limit, without fetching a calendar booked years ahead in full on every resync.
 */
export const ROLLING_FETCH_WINDOW_DAYS_AHEAD = 90

/**
 * The ceiling of the rolling calendar fetch window: `now + ROLLING_FETCH_WINDOW_DAYS_AHEAD`. A
 * fetch size and nothing more — an event outside it is not asked about, never treated as removed.
 */
export function rollingFetchWindowEnd(): Date {
   return new Date(Date.now() + ROLLING_FETCH_WINDOW_DAYS_AHEAD * 24 * 60 * 60 * 1000)
}

export type GapDateFormat = 'date-standard' | 'date-full-month' | 'date-full-day' | 'date-no-year' | 'date-full' | 'date-iso' | 'short-date-optional-year'
export type GapDateTimeFormat = 'date-time-standard'
interface FormatAsRelativeTimeParams {
   compareTime: Date
   baseTime?: Date
   withTime?: boolean
}

import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'

TimeAgo.addDefaultLocale(en)

export default class DateUtils {
   static DATE_FORMAT = 'd MMM y'
   static DATE_FORMAT_NO_YEAR = 'd MMM'
   static TIME_FORMAT = 'h:mm aaa'
   static DATE_TIME_FORMAT = 'dd/MMM/yyyy HH:mm'
   static DATE_FULL = 'EEE d MMM, yyyy h:mm a'
   static DATE_ISO = 'yyyy-MM-dd'

   static dayDifference = (date1: string | Date, date2?: string | Date) => {
      date1 = typeof date1 === 'string' ? new Date(date1) : date1
      date2 = !date2 ? new Date() : typeof date2 === 'string' ? new Date(date2) : date2
      return Math.round((date1.getTime() - date2.getTime()) / (1000 * 3600 * 24))
   }

   static addTime = (
      date: Date | string,
      time: {
         seconds?: number
         minutes?: number
         hours?: number
         days?: number
      }
   ) => {
      if (typeof date === 'string') {
         date = new Date(date)
      }
      let timeToAdd = 0
      if (time.seconds) timeToAdd += time.seconds * Timeouts.second
      if (time.minutes) timeToAdd += time.minutes * Timeouts.minute
      if (time.hours) timeToAdd += time.hours * Timeouts.hour
      if (time.days) timeToAdd += time.days * Timeouts.day

      return new Date(date.getTime() + timeToAdd)
   }

   static yesterday = () => {
      return DateUtils.addTime(new Date(), { days: -1 })
   }

   /**
    * Formats a timezone-agnostic date in ISO format
    * Moved from frontend/src/reusable/utils/date-utils.ts
    * @param dateString the date, as stored in the database
    */
   static formatTimezoneAgnosticISODate = (dateString: string) => {
      if (!dateString) return ''

      const date = new Date(dateString)
      const localTzOffset = date.getTimezoneOffset() * 60000

      const tzAgnosticDate = new Date(date.getTime() - localTzOffset)

      return tzAgnosticDate.toISOString()
   }

   /**
    * Adds working days to the current date or the given date.
    * Moved from frontend/src/reusable/utils/date-utils.ts
    *
    * Examples:
    * - Adding 4 working days from a Thursday results in next Wednesday
    * - Adding 4 working days from a Friday results in next Thursday
    * - Adding 4 working days from a Saturday results in next Thursday
    * - Adding 4 working days from a Sunday results in next Thursday
    * - Adding 4 working days from a Monday results in next Friday
    *
    * @param options Configuration object with days to add and optional start date
    */
   static addWorkingDays = (options: { days: number; startDate?: Date }): Date => {
      const date = options.startDate || new Date()
      const today = date.getDay()

      if (today === 6) {
         // Today is saturday, so add 1 day
         date.setDate(date.getDate() + 1)
      }

      for (let step = 0; step < options.days; step++) {
         date.setDate(date.getDate() + 1)
         const tomorrow = date.getDay()

         // We already went forward a day. Skip extra 2 days if tomorrow is 6(Saturday)
         if (tomorrow === 6) {
            date.setDate(date.getDate() + 2)
         }
      }

      return date
   }

   // Moved from delay-utils.ts - consolidated delay functions
   static wait = (ms: number): Promise<void> => {
      return new Promise(resolve => setTimeout(resolve, ms))
   }

   /**
    * Creates a random delay between min and max milliseconds
    * @param min - Minimum delay in milliseconds
    * @param max - Maximum delay in milliseconds
    * @returns Promise that resolves after the random delay
    */
   static randomDelay = (min: number, max: number): Promise<void> => {
      const delayMs = min + Math.random() * (max - min)
      return DateUtils.wait(delayMs)
   }

   /**
    * Creates an exponential backoff delay for retry scenarios
    * @param attempt - The attempt number (0-based)
    * @param baseDelayMs - Base delay in milliseconds (default: 1000)
    * @returns Promise that resolves after the calculated delay
    */
   static exponentialBackoffDelay = (attempt: number, baseDelayMs: number = 1000): Promise<void> => {
      const maxDelayMs = 30000
      const exponentialDelay = Math.min(baseDelayMs * Math.pow(2, attempt), maxDelayMs)
      const finalDelay = exponentialDelay + Math.random() * 1000
      return DateUtils.wait(finalDelay)
   }

   static timeoutInSeconds = (timeoutinMilliseconds: number) => {
      return timeoutinMilliseconds / 1000
   }

   static formatMonthAndYear = (date: Date) => {
      return date.toString() === 'Invalid Date' ? '' : format(date, 'MMMM yyyy')
   }

   static dateSorter = <T>(dateGetter: (type: T) => string | Date, order: 'asc' | 'desc' = 'desc') => {
      const getTime = (input: Date | string) => {
         const date = typeof input === 'object' ? input : new Date(input)
         return date.getTime()
      }

      return (a: T, b: T) => {
         const aTime = getTime(dateGetter(a))
         const bTime = getTime(dateGetter(b))

         return order === 'asc' ? compareAsc(aTime, bTime) : compareDesc(aTime, bTime)
      }
   }

   /**
    * Calendar date in an IANA timezone (e.g. from client `x-time-zone`).
    * Uses the same `GapDateFormat` mapping as {@link DateUtils.formatDate} (date-fns patterns).
    * Default format is `date-full-month`
    * Missing or invalid zone falls back to UTC when formatting so we can fail fast.
    */
   static formatDateInTimeZone = (date: Date | string, timeZone: string | undefined, dateFormat: GapDateFormat = 'date-full-month'): string => {
      const d = typeof date === 'string' ? new Date(date) : date
      if (d.toString() === 'Invalid Date') {
         return ''
      }
      const tz = timeZone?.trim() || 'UTC'
      return DateUtils.formatGapDateWithMode(d, dateFormat, { type: 'iana', timeZone: tz })
   }

   /** Parse a `YYYY-MM-DD` string at local midnight. Returns `null` for invalid input. */
   static parseDateIso = (iso: string): Date | null => {
      const [year, month, day] = iso.split('-').map(Number)
      if (!year || !month || !day) return null
      const date = new Date(year, month - 1, day)
      if (isNaN(date.getTime())) return null
      return date
   }

   /** Today formatted with `DateUtils.DATE_FORMAT` (e.g. `"15 Mar 2024"`), at local
    *  midnight. `offsetInDays` adds whole days. */
   static todayIso = (offsetInDays = 0): string => {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + offsetInDays)
      return format(date, DateUtils.DATE_FORMAT)
   }

   /** Friendly label for a `YYYY-MM-DD` date: "Mon, Jan 5". */
   static formatDateLabel = (iso: string): string => {
      const date = DateUtils.parseDateIso(iso)
      if (!date) return iso
      return format(date, 'EEE, MMM d')
   }

   static formatDate = (date: Date | string, dateFormat?: GapDateFormat) => {
      if (typeof date === 'string') {
         date = new Date(date)
      }

      if (date.toString() === 'Invalid Date') {
         return ''
      }

      return DateUtils.formatGapDateWithMode(date, dateFormat, { type: 'local' })
   }

   static formatDateTime = (dateTime: Date | string, options?: { hideYearIfCurrentYear?: boolean; includeSeconds?: boolean }) => {
      if (typeof dateTime === 'string') {
         dateTime = new Date(dateTime)
      }

      if (dateTime.toString() === 'Invalid Date') {
         return ''
      }

      const timeFormat = options?.includeSeconds ? 'h:mm:ss aaa' : DateUtils.TIME_FORMAT

      if (options?.hideYearIfCurrentYear && dateTime.getFullYear() === new Date().getFullYear()) {
         return format(dateTime, `${DateUtils.DATE_FORMAT_NO_YEAR} 'at' ${timeFormat}`)
      }
      return format(dateTime, `${DateUtils.DATE_FORMAT} 'at' ${timeFormat}`)
   }

   static formatAsRelativeTime = ({ compareTime, baseTime = new Date(), withTime = true }: FormatAsRelativeTimeParams) => {
      const secondsDifference = differenceInSeconds(baseTime, compareTime)
      const minutesDifference = secondsDifference / 60
      const hoursDifference = minutesDifference / 60
      const dayStartOffset = differenceInDays(startOfDay(baseTime), startOfDay(compareTime))

      if (secondsDifference < 60) {
         return 'just now'
      }
      if (minutesDifference < 2) {
         return 'a minute ago'
      }
      if (hoursDifference < 1) {
         return `${Math.floor(minutesDifference)} minutes ago`
      }
      if (hoursDifference < 2) {
         return 'an hour ago'
      }
      if (dayStartOffset < 1) {
         return `today${withTime ? ` at ${format(compareTime, DateUtils.TIME_FORMAT)}` : ''}`
      }
      if (dayStartOffset < 2) {
         return `yesterday at ${format(compareTime, DateUtils.TIME_FORMAT)}`
      }
      if (dayStartOffset < 7) {
         return format(compareTime, `EEEE 'at' ${DateUtils.TIME_FORMAT}`)
      }

      return DateUtils.formatDateTime(compareTime)
   }

   static format = (time: Date | string) => {
      if (typeof time === 'string') {
         time = new Date(time)
      }

      return new TimeAgo('en-US').format(time)
   }

   private static formatInTimeZoneSafe(date: Date, timeZone: string, pattern: string): string {
      try {
         return formatInTimeZone(date, timeZone, pattern)
      } catch {
         return formatInTimeZone(date, 'UTC', pattern)
      }
   }

   /** Maps {@link GapDateFormat} to date-fns formatting, supporting both local runtime timezone and explicit IANA timezone. */
   private static formatGapDateWithMode(
      date: Date,
      dateFormat: GapDateFormat | undefined,
      mode: { type: 'local' } | { type: 'iana'; timeZone: string }
   ): string {
      const resolvedFormat = dateFormat ?? 'date-standard'

      const applyPattern = (pattern: string): string => {
         if (mode.type === 'local') {
            return format(date, pattern)
         }
         return DateUtils.formatInTimeZoneSafe(date, mode.timeZone, pattern)
      }

      switch (resolvedFormat) {
         case 'date-standard':
            return applyPattern(DateUtils.DATE_FORMAT)
         case 'short-date-optional-year': {
            if (mode.type === 'local') {
               return date.getFullYear() === new Date().getFullYear() ? applyPattern(DateUtils.DATE_FORMAT_NO_YEAR) : applyPattern(DateUtils.DATE_FORMAT)
            }
            const yearOfDate = Number(DateUtils.formatInTimeZoneSafe(date, mode.timeZone, 'yyyy'))
            const yearOfNow = Number(DateUtils.formatInTimeZoneSafe(new Date(), mode.timeZone, 'yyyy'))
            return yearOfDate === yearOfNow ? applyPattern(DateUtils.DATE_FORMAT_NO_YEAR) : applyPattern(DateUtils.DATE_FORMAT)
         }
         case 'date-full-month':
            return applyPattern('d MMMM y')
         case 'date-no-year':
            return applyPattern(DateUtils.DATE_FORMAT_NO_YEAR)
         case 'date-full':
            return applyPattern(DateUtils.DATE_FULL)
         case 'date-full-day':
            if (mode.type === 'local') {
               return date.toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
               })
            }
            return DateUtils.formatInTimeZoneSafe(date, mode.timeZone, 'EEEE, d MMMM y')
         case 'date-iso':
            return applyPattern(DateUtils.DATE_ISO)
      }
   }
}

export class Timeouts {
   static second = 1000
   static halfMinute = Timeouts.second * 30
   static minute = Timeouts.second * 60
   static hour = Timeouts.minute * 60
   static day = Timeouts.hour * 24
   static week = Timeouts.day * 7
   static backdropTransitionBuffer = 500
}

/**
 * Date-grouping helpers for the Meetings list, classifying in browser-local time so "Today" is the
 * viewer's local start-of-day. A dateless meeting lands in "No date"; everything else is upcoming
 * or past. These helpers own the bucketing rule, not the rendering.
 */

/** Which top-level list section a date falls into. `no-date` is reserved for a null/absent date. */
export type MeetingDateSection = 'no-date' | 'upcoming' | 'past'

/**
 * A resolved day/week group for a meeting or event date: its `section`, a `key` unique per
 * section and day/week, the heading `label`, and the `sortValue` groups are ordered by.
 */
export interface MeetingDateGroup {
   section: MeetingDateSection
   key: string
   label: string
   sortValue: number | null
}

/**
 * Short day-and-date label in browser-local time, e.g. `Fri, 15 Mar`. Used for
 * upcoming days beyond Tomorrow and for the last-week past days, where the bare
 * weekday name alone would be ambiguous across weeks.
 */
export function dayAndDateLabel(date: Date): string {
   return format(date, 'EEE, d MMM')
}

/**
 * Monday-start "Week of …" label in browser-local time, e.g. `Week of 10 Mar`.
 * The week start uses `weekStartsOn: 1` (Monday).
 */
export function weekOfLabel(date: Date): string {
   return `Week of ${format(startOfWeek(date, { weekStartsOn: 1 }), 'd MMM')}`
}

/** The "No date" group — a single fixed bucket pinned above Today. */
export function noDateGroup(): MeetingDateGroup {
   return { section: 'no-date', key: 'no-date', label: 'No date', sortValue: null }
}

/**
 * Bucket a meeting/event date into its day/week group, browser-local: `Today`, `Tomorrow`, a day and
 * date, a `Week of …` for anything older than the last 7 days, or the "No date" group.
 *
 * @param date the meeting date, or `null`/`undefined` for a dateless meeting
 * @param now the reference "now" (defaults to `new Date()`); pass explicitly in tests
 */
export function classifyMeetingDate(date: Date | string | null | undefined, now: Date = new Date()): MeetingDateGroup {
   if (date === null || date === undefined) return noDateGroup()

   const parsed = typeof date === 'string' ? new Date(date) : date
   if (parsed.toString() === 'Invalid Date') return noDateGroup()

   const dayStart = startOfDay(parsed)
   const todayStart = startOfDay(now)
   // Calendar-day delta (not 24h chunks) so DST transitions don't shift a day
   // into the wrong bucket. Positive = future, negative = past.
   const dayDelta = differenceInCalendarDays(dayStart, todayStart)
   const dayKey = format(dayStart, 'yyyy-MM-dd')
   const dayStartMs = dayStart.getTime()

   if (dayDelta >= 0) {
      if (dayDelta === 0) return { section: 'upcoming', key: `upcoming-${dayKey}`, label: 'Today', sortValue: dayStartMs }
      if (dayDelta === 1) return { section: 'upcoming', key: `upcoming-${dayKey}`, label: 'Tomorrow', sortValue: dayStartMs }
      return { section: 'upcoming', key: `upcoming-${dayKey}`, label: dayAndDateLabel(dayStart), sortValue: dayStartMs }
   }

   // Past. The 7 most recent past days (yesterday … 7 days ago, delta -1 through
   // -7) group by day; older dates group into their Monday-start week.
   if (dayDelta >= -7) {
      return { section: 'past', key: `past-day-${dayKey}`, label: dayAndDateLabel(dayStart), sortValue: dayStartMs }
   }
   const weekStart = startOfWeek(dayStart, { weekStartsOn: 1 })
   return { section: 'past', key: `past-week-${format(weekStart, 'yyyy-MM-dd')}`, label: weekOfLabel(dayStart), sortValue: weekStart.getTime() }
}
